<template>
  <div class="cms-item-form">
    <drawer-dialog-controls
      :is-loading="isLoading"
      :is-pristine="isPristine"
      :is-disabled="!isFormValid"
      @close="$emit('close')"
      @save="onSave"
    >
      {{ isNew ? 'New Item' : `Edit Item #${item.item_id}` }}
      <v-chip v-if="schemaVersion" small outlined color="white" class="ml-2">
        schema v{{ schemaVersion }}
      </v-chip>
    </drawer-dialog-controls>

    <div class="form-content">
      <v-form v-model="isFormValid">
        <!-- System fields -->
        <v-card>
          <v-card-title>Item Settings</v-card-title>
          <v-divider />
          <v-container>
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="localItem.type_id"
                  :items="types"
                  item-text="name"
                  item-value="type_id"
                  label="Type"
                  dense
                  outlined
                  :rules="[v => !!v || 'Type is required']"
                  :disabled="!isNew"
                  @change="loadSchema"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="localItem.name"
                  label="Name"
                  dense
                  outlined
                  :rules="[v => !!v || 'Name is required']"
                  @input="onNameInput"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="localItem.slug"
                  label="Slug"
                  dense
                  outlined
                  :rules="[v => !!v || 'Slug is required']"
                  @input="slugManuallyEdited = !!$event"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="localItem.status"
                  :items="STATUS_OPTIONS"
                  label="Status"
                  dense
                  outlined
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="localItem.environments"
                  :items="ENVIRONMENT_OPTIONS"
                  label="Environments"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="6">
                <v-select
                  v-model="localItem.category_ids"
                  :items="categoryOptions"
                  item-text="name"
                  item-value="category_id"
                  item-disabled="disabled"
                  label="Categories"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  dense
                  outlined
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card>

        <!-- Content fields -->
        <v-card v-if="hasSchema" class="mt-4">
          <v-card-title>
            Content
            <v-spacer />
            <v-btn small text @click="showRawJson = !showRawJson">
              {{ showRawJson ? 'Form View' : 'JSON View' }}
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-container v-if="!showRawJson">
            <schema-field
              :fields="schema.fields"
              :fieldsets="schema.fieldsets"
              :value="dataObject"
              :root-value="dataObject"
              :locales="locales"
              :entity-id="localItem.entity_id"
              @input="dataObject = $event"
            />
          </v-container>
          <v-container v-else>
            <v-textarea
              v-model="rawDataJson"
              rows="15"
              auto-grow
              :error-messages="jsonError"
              style="font-family: monospace; font-size: 13px;"
            />
          </v-container>
        </v-card>

        <!-- Fallback raw JSON -->
        <v-card v-else-if="!schemaLoading" class="mt-4">
          <v-card-title>Data (JSON)</v-card-title>
          <v-divider />
          <v-container>
            <v-textarea
              v-model="rawDataJson"
              rows="15"
              auto-grow
              :error-messages="jsonError"
              style="font-family: monospace; font-size: 13px;"
            />
          </v-container>
        </v-card>
      </v-form>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { API } from '@/utils/api'
import { DrawerDialogControls } from '@/components/core/DrawerDialog'
import { ENVIRONMENT_OPTIONS, STATUS_OPTIONS } from '@/components/cms/constants'
import SchemaField from './SchemaField.vue'

export default {
  components: {
    DrawerDialogControls,
    SchemaField,
  },
  props: {
    item: Object,
    types: Array,
    isLoading: Boolean,
  },
  data() {
    return {
      localItem: {},
      categories: [],
      dataObject: {},
      schema: null,
      schemaVersion: null,
      locales: [],
      schemaLoading: false,
      showRawJson: false,
      isPristine: true,
      slugManuallyEdited: false,
      isFormValid: false,
      jsonError: '',
    }
  },
  computed: {
    STATUS_OPTIONS: () => STATUS_OPTIONS,
    ENVIRONMENT_OPTIONS: () => ENVIRONMENT_OPTIONS,
    isNew() {
      return !this.item.item_id
    },
    hasSchema() {
      return !!this.schema?.fields?.length
    },
    rawDataJson: {
      get() {
        return JSON.stringify(this.dataObject, null, 2)
      },
      set(val) {
        try {
          this.dataObject = JSON.parse(val)
          this.jsonError = ''
        } catch (e) {
          this.jsonError = 'Invalid JSON: ' + e.message
        }
      },
    },
    categoryOptions() {
      const activeIds = new Set(this.categories.map(c => c.category_id))
      const selected = this.localItem.category_ids || []
      const inactiveItems = selected
        .filter(id => !activeIds.has(id))
        .map(id => ({
          category_id: id,
          name: `#${id} (inactive)`,
          disabled: true,
        }))
      return [...this.categories, ...inactiveItems]
    },
  },
  created() {
    this.localItem = cloneDeep(this.item || {})
    if (!Array.isArray(this.localItem.category_ids)) {
      this.localItem.category_ids = []
    }
    this.parseDataObject()

    // Use pre-loaded schema and locales from item/get if available
    //в каких случаях появляются служебные поля?
    
    if (this.item._schema) {
      this.schema = this.item._schema
      this.schemaVersion = this.item._schema_version || null
    } else if (this.localItem.type_id) {
      this.loadSchema(this.localItem.type_id)
    }

    if (this.item._locales && this.item._locales.length) {
      this.locales = this.item._locales
    } else {
      this.loadLocales()
    }

    this.loadCategories()

    // Clean internal props from localItem
    delete this.localItem._schema
    delete this.localItem._schema_version
    delete this.localItem._locales

    // Mark slug as manually edited if item already has a slug
    if (this.localItem.slug) {
      this.slugManuallyEdited = true
    }


    this.$nextTick(() => {
      this.isPristine = true
    })
  },
  watch: {
    localItem: {
      deep: true,
      handler() {
        this.isPristine = false
      },
    },
    dataObject: {
      deep: true,
      handler() {
        this.isPristine = false
      },
    },
  },
  methods: {
    onNameInput(val) {
      if (!this.slugManuallyEdited) {
        this.localItem.slug = this.slugify(val || '')
      }
    },
    slugify(str) {
      return str
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
    },
    parseDataObject() {
      try {
        this.dataObject = JSON.parse(this.localItem.data || '{}')
      } catch (e) {
        this.dataObject = {}
      }
    },
    loadCategories() {
      if (!this.localItem.entity_id || !this.localItem.type_id) {
        this.categories = []
        return
      }
      API.call('/cms/category/list.json', {
        entity_id: this.localItem.entity_id,
        type_id: this.localItem.type_id,
        is_active: true,
      }).then(r => {
        this.categories = r.data.items || []
      }).catch(() => {
        this.categories = []
      })
    },
    loadLocales() {
      this.$store.dispatch('cms/main/loadLocales').then(locales => {
        this.locales = locales
      }).catch(() => {
        this.locales = []
      })
    },
    loadSchema(typeId) {
      if (!typeId) {
        this.schema = null
        return
      }
      this.schemaLoading = true
      API.call('/cms/type-schema/get.json', {
        type_id: typeId,
        current_only: true,
      }).then(r => {
        if (r.data && r.data.item && r.data.item.schema) {
          const raw = r.data.item.schema
          this.schema = typeof raw === 'string' ? JSON.parse(raw) : raw
        } else {
          this.schema = null
        }
      }).catch(() => {
        this.schema = null
      }).finally(() => {
        this.schemaLoading = false
      })
    },
    onSave() {
      this.localItem.data = JSON.stringify(this.dataObject)
      this.$emit('save', this.localItem)
    },
  },
}
</script>

<style scoped>
.cms-item-form {
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.form-content {
  background-color: #F2F2F2;
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}
</style>
